/**
 * История генераций одной сцены (пина или референса) для счётчика у строки и боковой панели
 * на `/generate`: чистые помощники над строками ленты `/api/fittings`. Здесь только
 * `import type`: модуль напрямую грузит `node --test`.
 */
import type { Gen } from "./project";

export type SceneGen = Pick<Gen, "generation_id" | "generation_model" | "status" | "variant_id" | "asset_path">;
export type Done = { variant_id: number; asset_path: string; model: string; generation_id: number };
export type SceneHistory = { done: Done[]; running: number; failed: number; truncated: boolean };

/** Потолок строк ленты `/api/fittings` (одна строка — вариант): `generate.feed_fittings(limit=200)`. */
export const FEED_LIMIT = 200;

/** Готовые картинки (новые сверху), число РАЗНЫХ генераций в работе / упавших и признак усечения ленты. */
export function sceneHistory(gens: readonly SceneGen[]): SceneHistory {
  const done: Done[] = [];
  const running = new Set<number>();
  const failed = new Set<number>();
  for (const g of gens) {
    if (g.status === "done") {
      if (g.variant_id !== null && g.asset_path)
        done.push({
          variant_id: g.variant_id,
          asset_path: g.asset_path,
          model: g.generation_model,
          generation_id: g.generation_id,
        });
    } else if (g.status === "queued" || g.status === "running") running.add(g.generation_id);
    else if (g.status === "failed") failed.add(g.generation_id);
  }
  done.sort((a, b) => b.variant_id - a.variant_id);
  return { done, running: running.size, failed: failed.size, truncated: gens.length >= FEED_LIMIT };
}

/** Модели с числом картинок: больше сверху, при равенстве по имени. */
export function modelCounts(done: readonly Done[]): [model: string, n: number][] {
  const m = new Map<string, number>();
  for (const d of done) m.set(d.model, (m.get(d.model) ?? 0) + 1);
  return [...m].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** Текст счётчика у строки сцены; склонение «фото» не меняется. */
export function counterText(h: SceneHistory): string {
  if (h.done.length === 0) return h.running > 0 ? `в работе ${h.running}` : "ещё не генерировали";
  return `сгенерировано ${h.done.length}${h.truncated ? "+" : ""} фото` + (h.running > 0 ? ` · в работе ${h.running}` : "");
}
